import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import { 
    Building2, 
    Plus, 
    ChevronRight,
    Trash2,
    ArrowLeft,
    Loader2,
    LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuperadminOrganizationsPage = () => {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        organization_code: '',
        primary_color: '#3b82f6'
    });

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const response = await axios.get(`${API}/superadmin/organizations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrganizations(response.data);
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
            toast.error('Failed to load organizations');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.organization_code) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Validate org code (lowercase, no spaces)
        const codeRegex = /^[a-z0-9-]+$/;
        if (!codeRegex.test(formData.organization_code)) {
            toast.error('Organization code must be lowercase letters, numbers, or hyphens only');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API}/superadmin/organizations`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Organization created successfully');
            setDialogOpen(false);
            setFormData({ name: '', organization_code: '', primary_color: '#3b82f6' });
            fetchOrganizations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create organization');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (orgId) => {
        try {
            await axios.delete(`${API}/superadmin/organizations/${orgId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Organization deleted');
            setDeleteId(null);
            fetchOrganizations();
        } catch (error) {
            toast.error('Failed to delete organization');
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/superadmin/login';
    };

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/superadmin/dashboard">
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                                <ArrowLeft size={20} />
                            </Button>
                        </Link>
                        <div>
                            <h1 className="text-lg font-semibold text-white">Organizations</h1>
                            <p className="text-xs text-slate-400">Manage all organizations</p>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        className="text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={handleLogout}
                    >
                        <LogOut size={18} className="mr-2" />
                        Sign Out
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6 animate-fade-in">
                    {/* Actions */}
                    <div className="flex justify-end">
                        <Button
                            className="bg-white text-slate-900 hover:bg-slate-100"
                            onClick={() => setDialogOpen(true)}
                            data-testid="create-org-btn"
                        >
                            <Plus size={18} className="mr-2" />
                            Create Organization
                        </Button>
                    </div>

                    {/* Organizations Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map(i => (
                                <Card key={i} className="bg-slate-800 border-slate-700 animate-pulse">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 bg-slate-700 rounded-lg mb-4" />
                                        <div className="h-6 bg-slate-700 rounded w-3/4 mb-2" />
                                        <div className="h-4 bg-slate-700 rounded w-1/2" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : organizations.length === 0 ? (
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-12 text-center">
                                <Building2 size={64} className="mx-auto text-slate-600 mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">
                                    No organizations yet
                                </h3>
                                <p className="text-slate-400 mb-6">
                                    Create your first organization to get started
                                </p>
                                <Button
                                    className="bg-white text-slate-900 hover:bg-slate-100"
                                    onClick={() => setDialogOpen(true)}
                                >
                                    <Plus size={18} className="mr-2" />
                                    Create Organization
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="orgs-grid">
                            {organizations.map((org) => (
                                <Card 
                                    key={org.id} 
                                    className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors"
                                    data-testid={`org-card-${org.id}`}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div 
                                                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                                                style={{ backgroundColor: org.primary_color }}
                                            >
                                                {org.name.charAt(0).toUpperCase()}
                                            </div>
                                            <Dialog open={deleteId === org.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                                                    onClick={() => setDeleteId(org.id)}
                                                    data-testid={`delete-org-${org.id}`}
                                                >
                                                    <Trash2 size={18} />
                                                </Button>
                                                <DialogContent className="bg-slate-800 border-slate-700">
                                                    <DialogHeader>
                                                        <DialogTitle className="text-white">Delete Organization</DialogTitle>
                                                        <DialogDescription className="text-slate-400">
                                                            Are you sure you want to delete "{org.name}"? This will delete all users, referrals, and rewards. This action cannot be undone.
                                                        </DialogDescription>
                                                    </DialogHeader>
                                                    <DialogFooter>
                                                        <Button variant="outline" onClick={() => setDeleteId(null)} className="border-slate-600 text-slate-300">
                                                            Cancel
                                                        </Button>
                                                        <Button 
                                                            variant="destructive"
                                                            onClick={() => handleDelete(org.id)}
                                                            data-testid="confirm-delete-org-btn"
                                                        >
                                                            Delete
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                        
                                        <h3 className="text-lg font-semibold text-white mb-1">
                                            {org.name}
                                        </h3>
                                        <p className="text-sm text-slate-400 mb-4">
                                            /{org.organization_code}
                                        </p>
                                        
                                        <Link to={`/superadmin/organizations/${org.id}`}>
                                            <Button 
                                                variant="outline" 
                                                className="w-full border-slate-600 text-slate-300 hover:text-white hover:border-slate-500"
                                                data-testid={`view-org-${org.id}`}
                                            >
                                                View Details
                                                <ChevronRight size={16} className="ml-2" />
                                            </Button>
                                        </Link>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create Organization</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Add a new organization to the platform
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-slate-200">Organization Name *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Acme Corporation"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="org-name-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="code" className="text-slate-200">Organization Code *</Label>
                            <Input
                                id="code"
                                value={formData.organization_code}
                                onChange={(e) => setFormData({ ...formData, organization_code: e.target.value.toLowerCase() })}
                                placeholder="acme"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="org-code-input"
                            />
                            <p className="text-xs text-slate-500">
                                Used in URL: /o/{formData.organization_code || 'code'}
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="color" className="text-slate-200">Primary Color</Label>
                            <div className="flex gap-3">
                                <input
                                    type="color"
                                    id="color"
                                    value={formData.primary_color}
                                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                    className="w-12 h-10 rounded-lg border border-slate-600 cursor-pointer bg-slate-700"
                                    data-testid="org-color-picker"
                                />
                                <Input
                                    value={formData.primary_color}
                                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                                />
                            </div>
                        </div>
                        
                        <DialogFooter className="pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setDialogOpen(false)}
                                className="border-slate-600 text-slate-300"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-white text-slate-900 hover:bg-slate-100"
                                disabled={submitting}
                                data-testid="save-org-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Organization'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
